import React, { useState, useLayoutEffect, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { STRINGS } from 'config';

const { PENDING, IDLE, FAILED, SUCCEEDED } = STRINGS.REQUEST_STATUS;

/**
 * Shows loader for at least 1 second or as long as it takes for the response to return.
 */
interface LoaderRendererProps {
  children: React.ReactNode;
  loaderComponent: React.ReactNode;
  loadingState: string;
  delayLoader?: boolean;
}

export const LoaderRenderer = ({ children, loaderComponent, loadingState, delayLoader }: LoaderRendererProps) => {
  const DELAY_MILLISECONDS = delayLoader ? 400 : 0;
  const [showSuspenseLoader, setShowSuspenseLoader] = useState(false);
  const [hasDelayElapsed, setHasDelayElapsed] = useState(false);
  const showLoader = loadingState === PENDING && (!delayLoader || hasDelayElapsed);
  const timer = useRef<NodeJS.Timeout | null>(null);
  useLayoutEffect(() => {
    if (loadingState === PENDING) {
      setHasDelayElapsed(false);
      timer.current = setTimeout(() => {
        setShowSuspenseLoader(true);
        setHasDelayElapsed(true);
        timer.current = setTimeout(() => {
          setShowSuspenseLoader(false);
        }, 1000);
      }, DELAY_MILLISECONDS);
    }
  }, [loadingState, setShowSuspenseLoader, DELAY_MILLISECONDS]);
  useLayoutEffect(() => {
    if (loadingState === IDLE) {
      setShowSuspenseLoader(false);
      if (timer.current) {
        clearTimeout(timer.current as NodeJS.Timeout);
      }
    }
    if ((loadingState === FAILED || loadingState === SUCCEEDED) && !showSuspenseLoader) {
      clearTimeout(timer.current as NodeJS.Timeout);
    }
  }, [loadingState, setShowSuspenseLoader, showSuspenseLoader]);
  useEffect(() => () => {
    clearTimeout(timer.current as NodeJS.Timeout);
  }, []);
  return <>{(showLoader || showSuspenseLoader) ? loaderComponent : children}</>;
};

LoaderRenderer.propTypes = {
  /**
   * This should be one of the STRINGS.REQUEST_STATUS statuses.
   */
  loadingState: PropTypes.oneOf([PENDING, IDLE, FAILED, SUCCEEDED]).isRequired,
  /**
   * Component to be shown whilst the {children} are loading.
   */
  loaderComponent: PropTypes.node.isRequired,
  /**
   * Determines whether we delay showing the loader (in the case that the network connection is fast, we don't need to show the loader).
   */
  delayLoader: PropTypes.bool,
};

LoaderRenderer.defaultProps = {
  delayLoader: true,
};
